import React from 'react';
import { Check, Flag } from '@mui/icons-material';
import { Box, CircularProgress, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { CollaborativeChartsReview, CollaborativePhysicianMatch } from '@uplift/api';
import { DateFormats, ROUTING_CONFIG } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { LeftPanelClose, LeftPanelOpen } from '@uplift/icons';
import { stateQuery, useCollaborativeChartsReviewClinicalFlagsQuery, useCollaborativeChartsReviewItemsQuery, userDisplayNameQuery, useSubmitCollaborativeChartsReviewMutation } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AsyncButton, Breadcrumb, Breadcrumbs, Button, Chip, useAlert } from '@uplift/ui-v2';
import { ReviewBlock } from './ReviewBlock';
import { ReviewSummaryBlock } from './ReviewSummaryBlock';
import { UserName } from './UserName';
import { useHistory } from 'react-router';
import { useSuspenseQueries } from '@tanstack/react-query';


export const ChartsReviewSection: React.VFC<{
  collaborativeMatch: CollaborativePhysicianMatch;
  chartsReview: CollaborativeChartsReview;
}> = ({ collaborativeMatch, chartsReview }) => {
  const history = useHistory();
  const timezone = useTimezone();
  const alert = useAlert();
  const { data: chartsReviewItems } = useCollaborativeChartsReviewItemsQuery(chartsReview.id);
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReview.id);
  const [
    { data: displayName },
    { data: state }
  ] = useSuspenseQueries({
    queries: [
      userDisplayNameQuery(collaborativeMatch.providerId),
      stateQuery(collaborativeMatch.stateId),
    ]
  });
  const submitCollaborativeChartsReviewMutation = useSubmitCollaborativeChartsReviewMutation(chartsReview.id);
  const [ selectedChartResourceId, setSelectedChartResourceId ] = React.useState<string | null>(chartsReviewItems?.[0].resourceId ?? null);
  const [ sidebarShown, setSidebarShown ] = React.useState(true);
  const reviewDateStart = CalendarDateService.date(chartsReview.period.start, DateFormats.ISO_DATE);
  const reviewDateEnd = CalendarDateService.date(chartsReview.period.end, DateFormats.ISO_DATE);
  const expireAt = CalendarDateService.date(chartsReview.expireAt, DateFormats.ISO_DATE);
  const expired = expireAt.isBefore(CalendarDateService.date());

  const currentProgress = chartsReviewItems.filter(c => c.completed).length;
  const chartsReviewItemsAmount = chartsReviewItems.length ?? 0;
  const selectedChartsReviewItem = chartsReviewItems.find(c => c.resourceId === selectedChartResourceId);
  const allChartsReviewed = chartsReviewItemsAmount === currentProgress;

  return (
    <>
      <Box
        sx={{
          bgcolor: 'common.white',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Breadcrumbs>
          <Breadcrumb to={ROUTING_CONFIG.collaborationManage}>Manage</Breadcrumb>
          <Breadcrumb currentPage>Chart Review</Breadcrumb>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', mt: 1 }}>
          <Box sx={{ mr: 1 }}>
            <IconButton size="small" onClick={() => setSidebarShown(!sidebarShown)}>
              {sidebarShown ? <LeftPanelClose /> : <LeftPanelOpen />}
            </IconButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" gap={8} sx={{ mt: '2px' }}>
              <Typography variant="h4" sx={{ fontWeight: '600 !important' }}>Chart Review for <UserName providerId={collaborativeMatch.providerId} /></Typography>
              <Typography fontWeight={400} color="textHint" sx={{ pt: '3px' }}>{displayName.suffix ? `${displayName.suffix} | ` : null}{state.fullName}</Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mt: 1 }}>
                {chartsReview.submittedAt ? (
                  <>
                    <Typography variant="h5" color="textSecondary">{CalendarDateService.format(reviewDateStart, 'MMMM D', 'MMMM D, YYYY')} - {CalendarDateService.format(reviewDateEnd, 'MMMM D', 'MMMM D, YYYY')}</Typography>
                    <Chip label="Submitted" size="small" color="primary" />
                  </>
                ) : (
                  <>
                    <Typography variant="h5" color="textSecondary">{CalendarDateService.format(reviewDateStart, 'MMMM D', 'MMMM D, YYYY')}</Typography>
                    <Chip label="Open" size="small" color="info" />
                    <Typography>{expired ? 'Overdue' : 'Due'} {expireAt.fromNow()}</Typography>
                  </>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress color="primary" size={20} thickness={6} enableTrackSlot variant="determinate" value={chartsReviewItemsAmount ? 100 / chartsReviewItemsAmount * currentProgress : 0} />
                  <Typography variant="body1"><strong>{currentProgress}</strong> / <strong>{chartsReviewItemsAmount}</strong> charts reviewed</Typography>
                </Box>
                {chartsReview.submittedAt ? (
                  <Button variant="contained" disabled>Submitted at {CalendarDateService.date(chartsReview.submittedAt).tz(timezone).format(DateFormats.DATETIME)}</Button>
                ) : (
                  <AsyncButton
                    variant="contained"
                    color="primary"
                    disabled={!allChartsReviewed}
                    onClick={async () => {
                      await submitCollaborativeChartsReviewMutation.mutateAsync();
                      alert.success('Charts Review submitted!');
                      history.push(ROUTING_CONFIG.collaborationManage);
                    }}
                  >
                    Submit Chart
                  </AsyncButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box bgcolor="grey.100" flex={1} height="100%" display="flex">
        {sidebarShown && (
          <>
            <Box bgcolor="common.white" width="240px">
              <List>
                {chartsReviewItems.map((chart, index) => (
                  <ListItemButton
                    key={chart.resourceId}
                    selected={chart.resourceId === selectedChartResourceId}
                    onClick={() => setSelectedChartResourceId(chart.resourceId)}
                  >
                    <ListItemText primary={`Chart ${index + 1}`} />
                    <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                      {clinicalReviewClinicalFlagByResource?.[chart.resourceId] ? <Flag color="warning" sx={{ mr: 0.5 }} /> : null}
                      {chart.completed && (
                        <Check sx={{ color: 'success.main' }} />
                      )}
                    </ListItemIcon>
                  </ListItemButton>
                ))}
                <ListItemButton
                  onClick={() => setSelectedChartResourceId('REVIEW_SUMMARY')}
                  selected={selectedChartResourceId === 'REVIEW_SUMMARY'}
                >
                  <ListItemText primary="Review Summary" />
                </ListItemButton>
              </List>
            </Box>
            <Box>
              <Divider orientation="vertical" />
            </Box>
          </>
        )}
        <Box sx={{ p: 2, pb: 0, flex: 1, display: 'flex' }}>
          {selectedChartsReviewItem && (
            <ReviewBlock
              key={selectedChartsReviewItem.resourceId}
              chartsReviewItem={selectedChartsReviewItem}
              disabled={!!chartsReview.submittedAt}
              onGoNext={() => {
                const currentSelectedIndex = chartsReviewItems.findIndex(c => c.resourceId === selectedChartResourceId);
                const newChart = chartsReviewItems[currentSelectedIndex + 1];

                if (newChart) {
                  setSelectedChartResourceId(newChart.resourceId);
                } else {
                  setSelectedChartResourceId('REVIEW_SUMMARY');
                }
              }}
            />
          )}
          {selectedChartResourceId === 'REVIEW_SUMMARY' && (
            <ReviewSummaryBlock chartsReview={chartsReview} chartsReviewItems={chartsReviewItems} />
          )}
        </Box>
      </Box>
    </>
  );
};
